import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateWorkspaceRoleDto,
  type UpdateWorkspaceRoleDto,
  type WorkspaceRoleListItemDto,
  WorkspaceRolesApi,
} from '@up2stay/up2promo-api-client';

import { useInvalidateWorkspacePermissions } from '@/core/hooks/use-permissions-query';

export const workspaceRolesQueryKey = (workspaceId?: string) =>
  ['workspace-roles', workspaceId] as const;

export function useWorkspaceRoles(workspaceId?: string) {
  return useQuery({
    queryKey: workspaceRolesQueryKey(workspaceId),
    queryFn: async (): Promise<WorkspaceRoleListItemDto[]> => {
      if (!workspaceId) return [];
      const response = await WorkspaceRolesApi.findAll({
        path: { workspaceId },
      });
      return response.data ?? [];
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

function useInvalidateRoleQueries(workspaceId?: string) {
  const queryClient = useQueryClient();
  const invalidatePermissions = useInvalidateWorkspacePermissions();

  return () => {
    if (workspaceId) {
      void queryClient.invalidateQueries({
        queryKey: workspaceRolesQueryKey(workspaceId),
      });
      invalidatePermissions(workspaceId);
    }
  };
}

export function useCreateRole(workspaceId?: string) {
  const invalidate = useInvalidateRoleQueries(workspaceId);

  return useMutation({
    mutationFn: async (body: CreateWorkspaceRoleDto) => {
      if (!workspaceId) throw new Error('workspaceId required');
      const response = await WorkspaceRolesApi.create({
        path: { workspaceId },
        body,
      });
      return response.data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateRole(workspaceId?: string) {
  const invalidate = useInvalidateRoleQueries(workspaceId);

  return useMutation({
    mutationFn: async ({
      roleId,
      body,
    }: {
      roleId: string;
      body: UpdateWorkspaceRoleDto;
    }) => {
      if (!workspaceId) throw new Error('workspaceId required');
      const response = await WorkspaceRolesApi.update({
        path: { workspaceId, roleId },
        body,
      });
      return response.data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useDeleteRole(workspaceId?: string) {
  const invalidate = useInvalidateRoleQueries(workspaceId);

  return useMutation({
    mutationFn: async (roleId: string) => {
      if (!workspaceId) throw new Error('workspaceId required');
      await WorkspaceRolesApi.remove({
        path: { workspaceId, roleId },
      });
    },
    onSuccess: () => invalidate(),
  });
}
